import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Box } from "../Box";
import "./tooltip.css";

type TooltipPosition = "top" | "bottom" | "left" | "right";

const GAP = 8;

/** Where the tooltip should sit relative to the trigger, in viewport coordinates. */
const positionStyle = (rect: DOMRect, position: TooltipPosition) => {
  switch (position) {
    case "bottom":
      return {
        top: rect.bottom + GAP,
        left: rect.left + rect.width / 2,
        transform: "translateX(-50%)",
      };
    case "left":
      return {
        top: rect.top + rect.height / 2,
        left: rect.left - GAP,
        transform: "translate(-100%, -50%)",
      };
    case "right":
      return {
        top: rect.top + rect.height / 2,
        left: rect.right + GAP,
        transform: "translateY(-50%)",
      };
    default:
      return {
        top: rect.top - GAP,
        left: rect.left + rect.width / 2,
        transform: "translate(-50%, -100%)",
      };
  }
};

/**
 * The tooltip text renders through a portal to <body> rather than as a plain
 * absolutely-positioned child, so it can't be clipped by an ancestor with
 * `overflow: hidden` (e.g. ExpansionPanel) — inside one of those, a
 * non-portaled tooltip toggles to "visible" but stays invisible anyway.
 */
export const Tooltip = ({
  children,
  content,
  position = "top",
}: {
  children: React.ReactNode;
  content: React.ReactNode;
  position?: TooltipPosition;
}) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<Record<string, any> | null>(null);

  const show = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) setStyle(positionStyle(rect, position));
  };
  const hide = () => setStyle(null);

  return (
    <Box
      ref={triggerRef}
      className="tooltip"
      onMouseEnter={show}
      onMouseLeave={hide}
    >
      {children}
      {style &&
        createPortal(
          <div className="tooltip-portal-text" style={style}>
            {content}
          </div>,
          document.body,
        )}
    </Box>
  );
};
